import { useId, type MouseEvent } from 'react';
import { TooltipProvider } from '@voll-intelligence/ui-kit/ui/tooltip';
import { AirlineLogo } from '../../../shared/airline_logo/airline_logo.js';
import { arrivalDayOffset, fmtTime, fmtWeekdayDayMonth } from '../flight_format.js';
import type { FlightSegmentSource } from '../flight_offer_source.js';
import {
  KIND_GLYPH,
  StopPill,
  flightNumberLabel,
  stopCondition,
  type FlightLegKind,
} from './flight_leg.js';
import type { FlightOfferCopy, FlightOfferPlaces } from './offer_copy.js';

/** O que a perna empilhada lê do host: os textos, o idioma e o cadastro. */
export type StackLegText = {
  readonly copy: FlightOfferCopy;
  readonly locale: string;
  readonly places: FlightOfferPlaces;
};

/**
 * **A perna do celular** (#3874) — a quarta gramática da perna de voo, e a
 * única em utilitárias do DS: cabeçalho, horários em `text-heading` com o IATA
 * embaixo, e a conexão em três estados na coluna do meio. Nasceu dentro do
 * card de oferta empilhado (`offer_card_stack.tsx`) e saiu de lá porque a tela
 * "Escolha as tarifas" do celular (#3875) desenha o voo escolhido com a MESMA
 * anatomia — é o cartão de resumo dela.
 *
 * Aeroporto de cidade com mais de um (#4157) leva o NOME embaixo do IATA ("DAL" /
 * "Love Field") e no tooltip da pílula da escala: "DAL" sozinho não dizia que não
 * era Dallas/Fort Worth. Aeroporto único da cidade segue só com o código.
 */

/** O IATA da ponta e, quando a cidade tem mais de um aeroporto, o nome dele. */
function StackEndpoint({
  iata,
  places,
}: {
  readonly iata: string;
  readonly places: FlightOfferPlaces;
}) {
  const name = places.airportOf(iata);
  return (
    <>
      <span className="text-body font-medium text-foreground">{iata}</span>
      {name && (
        <span className="max-w-32 truncate text-detail text-muted-foreground" title={name}>
          {name}
        </span>
      )}
    </>
  );
}

/** Cabeçalho da perna no card do CHAT: glifo + "Ida/Volta/Voo · data" à
 *  esquerda, logo + nº do voo à direita. */
function StackDateHead({
  id,
  seg,
  kind,
  flightNumber,
  text,
}: {
  readonly id: string;
  readonly seg: FlightSegmentSource;
  readonly kind?: FlightLegKind;
  readonly flightNumber: string;
  readonly text: StackLegText;
}) {
  const { copy, locale } = text;
  const Glyph = kind ? KIND_GLYPH[kind] : KIND_GLYPH.outbound;
  const word = kind ? copy.leg.kind[kind] : copy.legSingle;
  return (
    <div className="flex items-center gap-1.5 pt-1">
      <Glyph size={16} aria-hidden />
      <span id={id} className="text-ui text-foreground">
        {word} · {fmtWeekdayDayMonth(seg.departAt, locale)}
      </span>
      <span className="ml-auto flex min-w-0 items-center gap-1 text-ui font-normal text-muted-foreground">
        <AirlineLogo
          {...(seg.carrier ? { iata: seg.carrier } : {})}
          {...(seg.carrierName ? { name: seg.carrierName } : {})}
          size={24}
        />
        <span className="truncate">{flightNumber}</span>
      </span>
    </div>
  );
}

/** Cabeçalho da perna na PÁGINA determinística: logo + companhia + números de
 *  voo, sem data (ela está no cabeçalho da busca). Sem `carrierName` o IATA
 *  responde pelo nome — melhor que um cabeçalho só de números. */
function StackCarrierHead({
  id,
  seg,
  flightNumber,
}: {
  readonly id: string;
  readonly seg: FlightSegmentSource;
  readonly flightNumber: string | undefined;
}) {
  return (
    <div className="flex min-w-0 items-center gap-1.5 pt-1">
      <AirlineLogo
        {...(seg.carrier ? { iata: seg.carrier } : {})}
        {...(seg.carrierName ? { name: seg.carrierName } : {})}
        size={24}
      />
      <span id={id} className="flex min-w-0 items-baseline gap-1.5 text-ui">
        <span className="shrink-0 text-foreground">{seg.carrierName ?? seg.carrier}</span>
        {/* O espaço é o separador do NOME acessível ("LATAM LA 3089"); no flex ele
            não ocupa lugar — o vão visual é o `gap`. */}
        {flightNumber && ' '}
        {flightNumber && (
          <span className="truncate font-normal text-muted-foreground">{flightNumber}</span>
        )}
      </span>
    </div>
  );
}

/**
 * Uma perna do card do celular. Sem `kind` (só ida) o cabeçalho abre com a
 * companhia e os números de voo (página determinística) ou, com `head="date"`
 * (card do chat), diz "Voo · data" — no desktop a perna única não tem palavra
 * nenhuma, mas aqui o cabeçalho É a linha de abertura do card e ficaria só com
 * a data. Ver `variant` em `FlightOfferRowProps`.
 *
 * A coluna da conexão tem três estados — ver `StackConnection`.
 */
export function StackLeg({
  seg,
  stops,
  flightNumber,
  kind,
  head = 'carrier',
  onDetail,
  onSeats,
  text,
}: {
  readonly seg: FlightSegmentSource;
  readonly stops: number;
  readonly flightNumber?: string;
  readonly kind?: FlightLegKind;
  /** `none`: sem cabeçalho — quem hospeda já abriu o cartão (o resumo do voo
   *  escolhido na tela de tarifas, #3875, põe logo + nº + cabine em cima). */
  readonly head?: 'date' | 'carrier' | 'none';
  readonly onDetail?: () => void;
  /** "Assentos" — a outra porta do drawer de detalhe. Ausente em trecho com
   *  escala (o mapa é por identidade de perna), como nas outras gramáticas. */
  readonly onSeats?: () => void;
  readonly text: StackLegText;
}) {
  const { copy } = text;
  const headId = useId();
  const dayOffset = arrivalDayOffset(seg.departAt, seg.arriveAt);
  const nextDayLabel = dayOffset > 0 ? copy.leg.arrivesNextDay(dayOffset) : '';
  return (
    <div {...(head === 'none' ? {} : { role: 'group', 'aria-labelledby': headId })}>
      {head === 'none' ? null : head === 'carrier' && !kind ? (
        <StackCarrierHead id={headId} seg={seg} flightNumber={flightNumber} />
      ) : (
        <StackDateHead
          id={headId}
          seg={seg}
          kind={kind}
          flightNumber={flightNumber ?? flightNumberLabel(seg.carrier, seg.flightNumber)}
          text={text}
        />
      )}
      <div className={`flex items-center gap-2 ${head === 'none' ? 'pt-1' : 'pt-3'}`}>
        <div className="grid gap-0.5">
          <span className="text-heading text-foreground tabular-nums">{fmtTime(seg.departAt)}</span>
          <StackEndpoint iata={seg.from} places={text.places} />
        </div>
        <StackConnection seg={seg} stops={stops} text={text} />
        <div className="grid justify-items-end gap-0.5 text-right">
          <span className="text-heading text-foreground tabular-nums">
            {fmtTime(seg.arriveAt)}
            {dayOffset > 0 && (
              <sup
                className="ml-0.5 text-detail font-medium text-danger-strong"
                role="img"
                title={nextDayLabel}
                aria-label={nextDayLabel}
              >
                +{dayOffset}
              </sup>
            )}
          </span>
          <StackEndpoint iata={seg.to} places={text.places} />
        </div>
      </div>
      {/* `stopPropagation`: no resultado determinístico o corpo do card é alvo
          de toque (#3893), e "Detalhes" não pode selecionar o trecho de carona. */}
      {(onDetail ?? onSeats) && (
        <div className="flex gap-3 pt-1.5">
          {onDetail && (
            <button type="button" className={LINK} onClick={stopping(onDetail)}>
              {copy.leg.viewDetail}
            </button>
          )}
          {onSeats && (
            <button type="button" className={LINK} onClick={stopping(onSeats)}>
              {copy.seats}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const stopping = (fn: () => void) => (e: MouseEvent) => {
  e.stopPropagation();
  fn();
};

const LINK =
  'text-ui text-muted-foreground underline underline-offset-[3px] focus-visible:rounded-xs focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';

/**
 * A coluna da conexão, nos três estados da issue: **direto** — "Direto" acima
 * da linha; **1 parada** — pílula sobre a linha com o aeroporto da escala e o
 * tempo de solo ("GIG · 1h 30min"), nada acima; **2+ paradas** — pílula com a
 * contagem ("2 paradas"), nada acima. A duração total fica sempre ABAIXO. Uma
 * parada SEM `connections` (ofertas não curadas: o produtor só busca as pernas
 * das curadas) cai na pílula de contagem — "1 parada" — em vez de sumir.
 *
 * A linha é um pseudo atrás da pílula, para o `<span>` crescer com ela e não
 * cobrir os textos vizinhos. A pílula reusa o `StopPill` do trilho denso pelo
 * tooltip quando trunca.
 */
function StackConnection({
  seg,
  stops,
  text,
}: {
  readonly seg: FlightSegmentSource;
  readonly stops: number;
  readonly text: StackLegText;
}) {
  const condition = stopCondition(stops, text.copy.leg);
  const stop = stops === 1 ? seg.connections?.[0] : undefined;
  // O nome do aeroporto da escala vai só no tooltip: visível, ele empurrava o tempo
  // de conexão para fora da elipse ("IAH George B…") na pílula estreita.
  const stopAirport = stop
    ? [stop.iata, text.places.airportOf(stop.iata)].filter(Boolean).join(' ')
    : '';
  const pill = stop ? (
    <StopPill
      variant="card"
      label={stop.layoverLabel ? `${stopAirport} · ${stop.layoverLabel}` : stopAirport}
      focusable={stopAirport !== stop.iata}
    >
      {stop.iata}
      {stop.layoverLabel && <span className="font-normal"> · {stop.layoverLabel}</span>}
    </StopPill>
  ) : stops > 0 ? (
    <StopPill variant="card" label={condition} />
  ) : null;
  return (
    <TooltipProvider delay={120}>
      <div className="flex min-w-0 flex-1 flex-col items-center gap-0.5 text-detail font-medium text-muted-foreground">
        {stops === 0 && <span>{condition}</span>}
        <span className="relative my-1 flex min-h-px w-[calc(100%-8px)] items-center justify-center before:absolute before:inset-x-0 before:top-1/2 before:h-px before:bg-border">
          {pill}
        </span>
        {seg.durationLabel && <span>{seg.durationLabel}</span>}
      </div>
    </TooltipProvider>
  );
}
